import { create, tsx } from '@dojo/framework/core/vdom';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { Summary } from '../../widgets/Summary';

import * as css from './UIDesign.m.css';
import FAQ from '../../widgets/FAQ';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import Text from '../../widgets/Text';
import ColumnSummary from '../../widgets/ColumnSummary';
import WhySitePen from '../../widgets/WhySitePen';

const designImg = require('../../assets/images/illustration/design.svg');
const designToolsImg = require('../../assets/images/logo-illustration/design-tools.svg');
const uiDesignImg = require('../../assets/images/icon-illustration/ui-design.svg');
const eyeImg = require('../../assets/images/icon-illustration/eye.svg');
const softwareConsultingDesign = require('../../assets/images/icon-illustration/software-consulting-design.svg');
const frontEndToolsImg = require('../../assets/images/icon-illustration/front-end-tools.svg');
const consultingImg = require('../../assets/images/illustration/consulting.svg');
const dependImg = require('../../assets/images/illustration/phone-graphs.svg');

const factory = create();

export const UIDesign = factory(function UIDesign() {
	return (
		<div classes={css.root}>
			<Head
				title="Enterprise Application UI Design | SitePen"
				description="SitePen specializes in enterprise UI design as part of our software development process to create the best possible products for our customers."
			/>

			<Hero background="level3Blue">
				{{
					xlTitle: 'UI Design',
					blurb:
						'From proofs-of-concept to fullstack feature development to legacy re-engineering, companies rely on our agility and expertise in all areas of the software development lifecycle. Learn how we help our customers meet their development objectives successfully and with confidence.'
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse paddingBottom="md">
						{{
							title: 'Create the Best User Interactions',
							blurb: (
								<virtual>
									<p>
										User interface design (sometimes referred to as user
										interaction design) is the art of creating a visual design
										language for your product. Whether it’s navigation, buttons,
										form elements, or the typography throughout your app, each
										of these things should work together to create a consistent
										user experience that is clear and easy to understand.
										Products that have a strong UI design feel familiar to the
										customer and help them utilize each feature with minimal
										friction. Much like learning any language, there is a visual
										“vocabulary” associated with your product. Making that
										vocabulary consistent and clear is paramount to a clean user
										experience.
									</p>
									<p>
										Alongside a clean user experience, UI design provides a
										visual aesthetic that allows your users to identify your
										products, creating greater brand loyalty and awareness. Your
										enterprise app is more than just a logo in the navigation
										bar. It’s how all of the elements look and feel and work
										together. This is even more important for platforms with
										multiple products. A strong UI design can be incorporated
										into multiple products or apps, making it easy for users to
										switch from one to another.{' '}
									</p>
								</virtual>
							),
							image: <Image src={designImg} size="lg" />
						}}
					</Summary>
					<Summary>
						{{
							title: 'Enterprise Application UI Design',
							blurb: (
								<virtual>
									<p>
										Strong user interface design is even more important to the
										enterprise space. Enterprise software incorporates a greater
										amount of complexity, larger user base, and more critical
										features than most consumer applications. Because of this,
										it’s even more important that your UI design is clean and
										easy to understand. Colors, typography, buttons, navigation,
										and other elements should be well articulated and easy to
										consume by the eye. They should feel consistent between
										different screens and features, making the user feel at ease
										as they navigate through the entire experience.
									</p>
									<p>
										Another important aspect of enterprise UI design is that
										enterprise software often incorporates an entire suite of
										products or platforms. If each of your apps are speaking a
										different visual language, it will create confusion for the
										user, make each of them more difficult to navigate, and
										stunt any ability to express your brand consistently to your
										customers.
									</p>
								</virtual>
							),
							image: <Image src={dependImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<WhySitePen reverse />
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<ColumnSummary columnCount={2}>
						{{
							title: 'UI Design Services',
							blurb: (
								<virtual>
									User interface design is a multi-faceted discipline that
									involves the smallest of pixel-perfect details while also being
									focused on big strategic thinking. It’s not simply creating a
									button or a card for the UI, it’s designing a flexible component
									that can be shared, reused, and adapted to multiple features of
									your enterprise application.
								</virtual>
							),
							items: [
								{
									title: 'Interface Design and Optimization',
									image: (
										<Image
											src={uiDesignImg}
											lazy
											align="center"
											size="icon"
											constrainedBy="height"
										/>
									),
									blurb: (
										<virtual>
											To your customers, it may just be a form on the screen.
											They may just click buttons in the navigation bar
											without consciously considering the visual selection of
											colors, typography, and style. They might not think
											about the padding and the margins that make the content
											easy to consume and navigate. But these are the things
											our design team obsesses over. Whether you have an
											existing UI design that needs to be updated or need
											something built from the ground up, we specialize in
											hand crafting beautiful and modern user interface
											components that are natural extensions of the user
											experience. These UI elements make your products easier
											to use and also extend the look and feel of your brand
											to your end user.
										</virtual>
									)
								},
								{
									title: 'Designing for Accessibility and WCAG Compliance',
									image: (
										<Image
											src={eyeImg}
											lazy
											align="center"
											size="icon"
											constrainedBy="height"
										/>
									),
									blurb: (
										<virtual>
											It is ever more important that the products we build are
											accessible to those with disabilities and other
											limitations. Software is moving toward the same
											regulation as other media, which require it to be usable
											to everyone regardless of any physical limitations. At
											SitePen we champion efforts to make the web and software
											applications open and accessible to everyone. This
											includes our approach to visual design and user
											interfaces. We design components and elements that are
											in line with current WCAG compliance standards. We
											consider color contrast, focus states, and typography
											that allow the app to be used by anyone with visual
											impairments or who cannot rely on a mouse for
											navigation.
										</virtual>
									)
								},
								{
									title: 'Design Systems',
									image: (
										<Image
											src={frontEndToolsImg}
											lazy
											align="center"
											size="icon"
											constrainedBy="height"
										/>
									),
									blurb: (
										<virtual>
											A critical foundation to an extensible UI is a design
											system. A design system is a library of components and
											elements that can be reused and repurposed throughout
											the entire application. Rather than creating styles and
											elements for each view, a design system gives pieces of
											UI that can be created once and reused everywhere. This
											is a huge win for both designers and developers. Your
											design team will be able to focus on the important
											aspects of the user experience and how to bring critical
											features to your customers. Your development team will
											be able to work faster, building code and components
											that are reusable and reducing the amount of waste and
											duplicated effort.
										</virtual>
									)
								},
								{
									title: 'Style Guides and Design Specifications',
									image: (
										<Image
											src={softwareConsultingDesign}
											lazy
											align="center"
											size="icon"
											constrainedBy="height"
										/>
									),
									blurb: (
										<virtual>
											A style guide goes hand in hand with a design system. It
											takes all of those well crafted UI elements and
											documents them in a way that your entire team or
											organization can reference. This gives everyone a single
											source of truth to build from, rather than each team
											designing and building in a silo. Using a style guide,
											developers can reference various design specifications
											to ensure what they build is consistent in look and feel
											with other parts of the application or with other
											products across your organization. This will enhance the
											user experience across your entire product offering,
											creating one easy to understand visual language.
										</virtual>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper>
					<Summary reverse>
						{{
							title: 'Technologies We Use',
							blurb: (
								<p>
									SitePen utilizes the best leading-edge technology for our design
									process. We have in-depth experience with industry standard
									applications such as Sketch, Figma, and the Adobe Suite. Our
									abilities are not limited to mockups and static screenshots, but
									also incorporate clickable prototypes built with InVision,
									Framer, Axure, and Figma. We leverage existing design libraries
									and design systems such as Google’s Material Design and
									Bootstrap, and we are experts at developing custom systems
									ourselves. Whatever the need, we have experience with the right
									tools and technology to deliver fast and beautiful results for
									your project.
								</p>
							),
							image: <Image src={designToolsImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							title: 'Choosing the Right UI Design Company',
							image: <Image src={consultingImg} lazy size="lg" />,
							blurb: (
								<virtual>
									<Text type="body">
										Choosing your design partner can be a critical part of your
										project’s success. Too many teams lack the experience to
										know what to look for in a UI design company. It’s important
										that you find someone who not only has a great portfolio,
										but also demonstrates that they can solve hard visual
										problems. A strong design team will challenge your ideas and
										show you new ways to approach your product.
									</Text>
									<Text type="body">
										The designers at SitePen have comprehensive experience
										solving tough challenges. We’ve worked closely with
										enterprise companies to build tools for their internal teams
										as well as their external customers that are engaging,
										intuitive, and easy to use. We bring experience, speed, and
										agility to the design process.
									</Text>
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ConnectStrip />
			</Section>
			<Section type="trailing" shaded>
				<FAQ title="Enterprise UI Design FAQs">
					{[
						{
							title: 'Why Is UI Design Important?',
							content: (
								<p>
									User Interface design is an important piece of any successful
									software app. It gives a face to your product that your
									customers can identify with, and it also provides important
									visual cues for how they can interact with the application.
									Having a consistent, clean UI design will not only make the
									customer experience fast and easy, but it will also reduce
									development time because pieces of the UI will be flexible and
									reusable across the app.
								</p>
							)
						},
						{
							title: 'How Long Does the Process Take?',
							content: (
								<p>
									UI design can be a matter of styling a few elements or pages, or
									it can be a robust engagement of defining an entire component
									library for your application. Whatever level of need your
									organization may have, our UI designers are fast at creating
									solutions that will bring the biggest impact to your product and
									customer base. Whether it’s a single sprint of effort or you’re
									looking for an ongoing design partner, SitePen has the
									experience to help build a beautiful and modern UI.
								</p>
							)
						},
						{
							title: 'Should My Company Think About UI Design?',
							content: (
								<p>
									Every software application needs a strong UI foundation. If this
									isn’t something that your company has, then it’s time to get
									some help. A clean UI will improve your customer’s experience,
									improve engagement, and help to create brand loyalty. It will
									also increase your development turnaround time, as elements in
									the product will be clearly defined and reusable across the
									entire experience.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default UIDesign;
